import { hardwareCheckIsOffline, needsAudio, needsWebcam } from '../../../../services/HardwareRequirements';
import liveSessioner from '../../../../services/livesession/LiveSessioner';
import livestreamActions from '../../../../actions/LivestreamActions';
import sessionReader from '../../../../store/readHelpers/SessionReader';
import { MIC_HEALTH_CHECK_STATUS } from '../../../../services/livesession/micHealthChecker';

const confirmHardwareSettingsSelector = {
    selectIsHardwareSettingsSelectorButtonDisabled(livestream, showWarningMissingAudioDevice, showWarningMissingWebcamDevice) {
        const hasMissingDevice = showWarningMissingAudioDevice || showWarningMissingWebcamDevice;
        return livestream.hasConfirmedHardwareSettings || hasMissingDevice;
    },

    selectShouldShowMicDiagnosisFailed(livestream) {
        return sessionReader.isRemoteProctoringMicHealthCheckEnabled() &&
            livestream.micHealthCheckStatus === MIC_HEALTH_CHECK_STATUS.FAILED;
    },

    selectShouldShowMicHealthCheckInProgress(livestream) {
        return livestream.micHealthCheckStatus === MIC_HEALTH_CHECK_STATUS.UNDIAGNOSED;
    },
    shouldShowMicHealthInformation() {
        return sessionReader.isRemoteProctoringMicHealthCheckEnabled();
    },
    shouldMuteMicWhenSoundTestIsOn() {
        return sessionReader.isRemoteProctoringMicHealthCheckEnabled();
    },
    selectShouldShowMicHealthStatus(livestream, showSelectAudioDevice) {
        return sessionReader.isRemoteProctoringMicHealthCheckEnabled() &&
            showSelectAudioDevice &&
            livestream.micHealthCheckStatus !== MIC_HEALTH_CHECK_STATUS.FAILED;
    },

    selectIsConfirmHardwareSettingsButtonDisabled(livestream, showWarningMissingAudioDevice, showWarningMissingWebcamDevice) {
        const hasMissingDevice = showWarningMissingAudioDevice || showWarningMissingWebcamDevice;
        const webCamHealthCheckFailed = sessionReader.isRemoteProctoringWebcamHealthCheckEnabled() &&
            livestream.webcamHealthCheckStatus !== livestreamActions.webcamHealthCheckStatus.PASSED;
        if (livestream.hasConfirmedHardwareSettings || hasMissingDevice) {
            return true;
        }

        if (webCamHealthCheckFailed) {
            return true;
        }
        const micHealthCheckNotPassed = sessionReader.isRemoteProctoringMicHealthCheckEnabled() &&
            livestream.micHealthCheckStatus !== MIC_HEALTH_CHECK_STATUS.PASSES;

        if (micHealthCheckNotPassed) {
            return true;
        }
        return false;
    },

    selectShowWarningMissingAudioDevice(audioDeviceId, isRemoteProctoringAudioEnabled) {
        return !audioDeviceId && isRemoteProctoringAudioEnabled;
    },

    selectShowWarningMissingWebcamDevice(webcamDeviceId, isRemoteProctoringWebCamEnabled) {
        return !webcamDeviceId && isRemoteProctoringWebCamEnabled;
    },

    selectShowSelectDevice(deviceList) {
        return !!deviceList && deviceList.length >= 1;
    },

    selectShouldShowWebcamHealthCheckInProgress(webcamHealthCheckStatus) {
        return webcamHealthCheckStatus === livestreamActions.webcamHealthCheckStatus.UNDIAGNOSED;
    },

    selectShouldShowWebcamHealthStatus(showSelectWebcamDevice, webcamHealthCheckStatus) {
        return sessionReader.isRemoteProctoringWebcamHealthCheckEnabled() &&
            showSelectWebcamDevice &&
            webcamHealthCheckStatus !== livestreamActions.webcamHealthCheckStatus.FAILED;
    },

    selectShouldShowMicrophoneAudioLevelMeter(showSelectAudioDevice) {
        return showSelectAudioDevice && sessionReader.isRemoteProctoringMicHealthCheckEnabled();
    },

    selectShouldShowHardwareInstructions(showSelectAudioDevice, showSelectWebcamDevice) {
        const isMicHealthCheckEnabled = sessionReader.isRemoteProctoringMicHealthCheckEnabled();
        const isWebcamHealthCheckEnabled = sessionReader.isRemoteProctoringWebcamHealthCheckEnabled();
        return (isMicHealthCheckEnabled && showSelectAudioDevice) ||
            (isWebcamHealthCheckEnabled && showSelectWebcamDevice);
    },

    shouldShowWebcamHealthInformation() {
        const isRemoteProctoringHealthCheckEnabled = sessionReader.isRemoteProctoringWebcamHealthCheckEnabled();
        return isRemoteProctoringHealthCheckEnabled;
    },

    shouldShowSpeakerInformation() {
        const isRemoteProctoringHealthCheckEnabled = sessionReader.isRemoteProctoringWebcamHealthCheckEnabled()
        || sessionReader.isRemoteProctoringMicHealthCheckEnabled();
        return isRemoteProctoringHealthCheckEnabled;
    },

    shouldShowWebcamDiagnosisFailed(webcamHealthCheckStatus) {
        const isRemoteProctoringWebcamHealthCheckEnabled = sessionReader.isRemoteProctoringWebcamHealthCheckEnabled();
        const shouldShowWebcamDiagnosisFailed = webcamHealthCheckStatus === livestreamActions.webcamHealthCheckStatus.FAILED;
        return isRemoteProctoringWebcamHealthCheckEnabled && shouldShowWebcamDiagnosisFailed;
    },

    shouldCompleteHardwarePrechecks() {
        return sessionReader.isRemoteProctoringHardwarePrecheckCompleted();
    },

    mapStateToProps(state) {
        const { livestream } = state;
        const {
            audioDeviceId,
            audioDevices,
            speakerDeviceId,
            speakerDevices,
            videoDevices,
            webcamDeviceId,
            webcamHealthCheckStatus,
        } = livestream;

        const showSelectAudioDevice = this.selectShowSelectDevice(audioDevices);
        const shouldShowMicrophoneAudioLevelMeter = this.selectShouldShowMicrophoneAudioLevelMeter(showSelectAudioDevice);
        const showSelectWebcamDevice = this.selectShowSelectDevice(videoDevices);

        const showWarningMissingWebcamDevice = this.selectShowWarningMissingWebcamDevice(webcamDeviceId, needsWebcam());
        const showWarningMissingAudioDevice = this.selectShowWarningMissingAudioDevice(audioDeviceId, needsAudio());
        const shouldShowWebcamDiagnosisFailed = this.shouldShowWebcamDiagnosisFailed(webcamHealthCheckStatus);
        const shouldShowWebcamHealthStatus = this.selectShouldShowWebcamHealthStatus(showSelectWebcamDevice, webcamHealthCheckStatus);
        const shouldShowWebcamHealthCheckInProgress = this.selectShouldShowWebcamHealthCheckInProgress(webcamHealthCheckStatus);
        const shouldShowHardwareInstructions = this.selectShouldShowHardwareInstructions(
            showSelectAudioDevice,
            showSelectWebcamDevice
        );
        const shouldShowWebcamHealthInformation = this.shouldShowWebcamHealthInformation();
        const shouldShowSpeakerInformation = this.shouldShowSpeakerInformation();
        const shouldShowMicDiagnosisFailed = this.selectShouldShowMicDiagnosisFailed(livestream);
        const shouldShowMicHealthStatus = this.selectShouldShowMicHealthStatus(livestream, showSelectAudioDevice);
        const shouldShowMicHealthCheckInProgress = this.selectShouldShowMicHealthCheckInProgress(livestream);
        const shouldCompleteHardwarePrechecks = this.shouldCompleteHardwarePrechecks();

        return {
            audioDeviceId,
            audioDevices,
            hardwareCheckIsOffline: hardwareCheckIsOffline(),
            isHardwareSettingsSelectorButtonDisabled: this.selectIsHardwareSettingsSelectorButtonDisabled(
                livestream, showWarningMissingAudioDevice, showWarningMissingWebcamDevice),
            isConfirmHardwareSettingsButtonDisabled: this.selectIsConfirmHardwareSettingsButtonDisabled(
                livestream, showWarningMissingAudioDevice, showWarningMissingWebcamDevice),
            shouldShowMicrophoneAudioLevelMeter,
            shouldShowHardwareInstructions,
            micHealthCheckStatus: livestream.micHealthCheckStatus,
            showSelectAudioDevice,
            showSelectSpeakerDevice: this.selectShowSelectDevice(speakerDevices),
            showSelectWebcamDevice,
            showWarningMissingAudioDevice,
            showWarningMissingWebcamDevice,
            shouldShowWebcamDiagnosisFailed,
            shouldShowWebcamHealthStatus,
            shouldShowWebcamHealthCheckInProgress,
            shouldShowMicDiagnosisFailed,
            shouldShowMicHealthStatus,
            shouldShowMicHealthCheckInProgress,
            speakerDeviceId,
            speakerDevices,
            videoDevices,
            webcamDeviceId,
            webcamHealthCheckStatus,
            webcamStream: liveSessioner.getGrantedStream(),
            shouldShowWebcamHealthInformation,
            shouldShowSpeakerInformation,
            shouldShowMicHealthInformation: this.shouldShowMicHealthInformation(),
            shouldMuteMicWhenSoundTestIsOn: this.shouldMuteMicWhenSoundTestIsOn(),
            shouldCompleteHardwarePrechecks,
        };
    },
};

export default confirmHardwareSettingsSelector;
